import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageCard } from "@/components/dashboard/PageCard";
import { ProfileSwitcher } from "@/components/dashboard/ProfileSwitcher";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState(localStorage.getItem("smartlinks_profile_id") || "");
  const [pages, setPages] = useState(null);
  const [showTypeDialog, setShowTypeDialog] = useState(false);
  const [creating, setCreating] = useState(false);

  const loadProfiles = useCallback(async () => {
    const { data } = await api.get("/profiles");
    setProfiles(data);
    const savedStillValid = data.some((p) => p.id === selectedProfileId);
    if (!savedStillValid && data.length > 0) {
      setSelectedProfileId(data[0].id);
    }
  }, [selectedProfileId]);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  useEffect(() => {
    if (selectedProfileId) {
      localStorage.setItem("smartlinks_profile_id", selectedProfileId);
    }
  }, [selectedProfileId]);

  const loadPages = useCallback(async () => {
    if (!selectedProfileId) return;
    const { data } = await api.get("/pages", { params: { profile_id: selectedProfileId } });
    setPages(data);
  }, [selectedProfileId]);

  useEffect(() => {
    loadPages();
  }, [loadPages]);

  const handleCreate = async (type) => {
    setCreating(true);
    try {
      const { data } = await api.post("/pages", { type, profile_id: selectedProfileId });
      navigate(`/builder/${data.id}`, { state: { justCreated: true } });
    } catch {
      toast.error("Could not create page");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this link page? This cannot be undone.")) return;
    await api.delete(`/pages/${id}`);
    toast.success("Page deleted");
    loadPages();
  };

  const handleCreateProfile = async (name) => {
    const { data } = await api.post("/profiles", { name });
    await loadProfiles();
    setSelectedProfileId(data.id);
    toast.success(`Profile "${name}" created`);
  };

  const handleUpdateProfile = async (id, patch) => {
    await api.put(`/profiles/${id}`, patch);
    await loadProfiles();
    toast.success("Profile updated");
  };

  const totalViews = pages?.reduce((s, p) => s + (p.views_count || 0), 0) || 0;
  const totalClicks = pages?.reduce((s, p) => s + (p.clicks_count || 0), 0) || 0;

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white" data-testid="dashboard-page">
      <nav className="border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <Link to="/" className="font-display font-black text-xl tracking-tight">SmartLinks</Link>
        <div className="flex items-center gap-4">
          <span className="text-sm text-slate-400 hidden sm:inline" data-testid="dashboard-user-email">{user?.email}</span>
          <Button variant="ghost" className="text-white hover:bg-white/10" onClick={logout} data-testid="logout-button">
            Log out
          </Button>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 py-10 space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div className="space-y-3">
            <h1 className="text-3xl sm:text-4xl font-black font-display tracking-tight">Your Link Pages</h1>
            {profiles.length > 0 && (
              <ProfileSwitcher
                profiles={profiles}
                selectedId={selectedProfileId}
                onSelect={setSelectedProfileId}
                onCreate={handleCreateProfile}
                onUpdate={handleUpdateProfile}
              />
            )}
            <p className="text-slate-400 text-sm">
              {pages ? `${pages.length} page${pages.length === 1 ? "" : "s"}` : "Loading..."} · {totalViews} total views · {totalClicks} total clicks
            </p>
          </div>
          <Button
            onClick={() => setShowTypeDialog(true)}
            className="bg-indigo-500 hover:bg-indigo-600 rounded-full h-11 px-6 font-bold"
            data-testid="create-link-page-button"
          >
            <i className="fa-solid fa-plus mr-2" /> Create Link Page
          </Button>
        </div>

        {pages && pages.length === 0 && (
          <div className="rounded-2xl border border-dashed border-white/15 py-20 text-center space-y-3" data-testid="dashboard-empty-state">
            <i className="fa-solid fa-link text-3xl text-white/20" />
            <p className="text-slate-400">No link pages yet for this profile. Create your first one to get started.</p>
          </div>
        )}

        {pages && pages.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="dashboard-pages-grid">
            {pages.map((p) => (
              <PageCard key={p.id} page={p} onDelete={handleDelete} />
            ))}
          </div>
        )}
      </main>

      <Dialog open={showTypeDialog} onOpenChange={setShowTypeDialog}>
        <DialogContent className="bg-[#121824] border-white/10 text-white" data-testid="page-type-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Choose a page type</DialogTitle>
            <DialogDescription>Pick the kind of campaign you're building.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 pt-2">
            <button
              onClick={() => handleCreate("music")}
              disabled={creating}
              className="rounded-xl border border-white/10 hover:border-indigo-400 hover:bg-indigo-500/10 transition-colors p-6 text-center space-y-2"
              data-testid="page-type-selector-music"
            >
              <i className="fa-solid fa-record-vinyl text-2xl text-indigo-400" />
              <p className="font-semibold">Music Release</p>
              <p className="text-xs text-slate-400">Single, EP, or album across streaming platforms</p>
            </button>
            <button
              onClick={() => handleCreate("event")}
              disabled={creating}
              className="rounded-xl border border-white/10 hover:border-emerald-400 hover:bg-emerald-500/10 transition-colors p-6 text-center space-y-2"
              data-testid="page-type-selector-event"
            >
              <i className="fa-solid fa-calendar-days text-2xl text-emerald-400" />
              <p className="font-semibold">Event / Tour</p>
              <p className="text-xs text-slate-400">Tour dates with city, venue and ticket links</p>
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
